import React, { useEffect, useMemo, useState } from 'react';
import {
  ShieldCheck, Play, RefreshCw, AlertCircle, AlertTriangle, Info, CheckCircle2,
  ArrowRight, Clock, Euro, X, ChevronRight,
  History as HistoryIcon, Sparkles, FileText, Download, Wand2
} from 'lucide-react';
import { apiCallJson, apiCall } from '../utils/api';
import { useTheme } from '../context/ThemeContext';

const SEVERITY_META = {
  critical: { label: 'Critical', color: '#ef4444', bg: 'rgba(239, 68, 68, 0.10)', Icon: AlertCircle, order: 0 },
  high:     { label: 'High',     color: '#f97316', bg: 'rgba(249, 115, 22, 0.10)', Icon: AlertTriangle, order: 1 },
  medium:   { label: 'Medium',   color: '#f59e0b', bg: 'rgba(245, 158, 11, 0.10)', Icon: AlertTriangle, order: 2 },
  low:      { label: 'Low',      color: '#3b82f6', bg: 'rgba(59, 130, 246, 0.10)', Icon: Info, order: 3 },
  info:     { label: 'Info',     color: '#64748b', bg: 'rgba(100, 116, 139, 0.10)', Icon: Info, order: 4 }
};

function formatCurrencyEur(value) {
  if (!value && value !== 0) return '—';
  return `€${Math.round(value).toLocaleString('nl-NL')}`;
}
function formatTimeMin(min) {
  if (!min && min !== 0) return '—';
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
}
function formatRelative(ts) {
  if (!ts) return '';
  const d = typeof ts === 'string' ? new Date(ts) : ts;
  const diff = Date.now() - d.getTime();
  const min = Math.round(diff / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min} min ago`;
  const h = Math.round(min / 60);
  if (h < 24) return `${h}h ago`;
  const days = Math.round(h / 24);
  if (days < 30) return `${days}d ago`;
  return d.toLocaleDateString();
}

export default function AccountAudit({ onNavigate }) {
  const { theme } = useTheme();
  const [latestAudit, setLatestAudit] = useState(null);
  const [history, setHistory] = useState([]);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState(null);
  const [activeAuditId, setActiveAuditId] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [showReport, setShowReport] = useState(false);

  // Load history (and most recent audit) on mount
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const data = await apiCallJson('/api/audit/history?limit=20');
        if (!alive) return;
        if (data?.success) {
          setHistory(data.audits || []);
          if (data.audits?.[0]?.id) {
            // Pull the full audit (with findings) for the most recent one
            const full = await apiCallJson(`/api/audit/${data.audits[0].id}`);
            if (full?.success && alive) {
              setLatestAudit(normalizeAudit(full.audit));
              setActiveAuditId(full.audit.id);
            }
          }
        }
      } catch (err) {
        // Non-fatal — first-time users will see the empty state
        console.warn('audit history load failed', err);
      }
    })();
    return () => { alive = false; };
  }, []);

  const runAudit = async () => {
    setRunning(true);
    setError(null);
    try {
      const data = await apiCallJson('/api/audit/run', {
        method: 'POST',
        body: {},
        timeout: 180000
      });
      if (data?.success && data.audit) {
        setLatestAudit(normalizeAudit(data.audit));
        setActiveAuditId(data.audit.id);
        // Refresh history so the new run shows up
        const h = await apiCallJson('/api/audit/history?limit=20');
        if (h?.success) setHistory(h.audits || []);
      } else {
        setError(data?.error || 'Audit failed');
      }
    } catch (err) {
      setError(err.message || 'Audit failed');
    } finally {
      setRunning(false);
    }
  };

  const loadAudit = async (auditId) => {
    setShowHistory(false);
    if (!auditId || auditId === activeAuditId) return;
    setRunning(true);
    try {
      const data = await apiCallJson(`/api/audit/${auditId}`);
      if (data?.success && data.audit) {
        setLatestAudit(normalizeAudit(data.audit));
        setActiveAuditId(auditId);
      }
    } catch (err) {
      setError(err.message || 'Could not load audit');
    } finally {
      setRunning(false);
    }
  };

  const findings = latestAudit?.findings || [];
  const groupedBySeverity = useMemo(() => groupBySeverity(findings), [findings]);

  return (
    <div style={{ padding: '32px 40px', color: theme.colors.text.primary, maxWidth: 1100, margin: '0 auto' }}>
      <Header
        theme={theme}
        running={running}
        latestAudit={latestAudit}
        onRun={runAudit}
        onShowHistory={() => setShowHistory(true)}
        onShowReport={() => setShowReport(true)}
        historyCount={history.length}
      />

      {error && <ErrorBanner theme={theme} message={error} onDismiss={() => setError(null)} />}

      {!latestAudit && !running && <EmptyState theme={theme} onRun={runAudit} />}

      {latestAudit && (
        <>
          <SummaryCards theme={theme} audit={latestAudit} />

          {findings.length === 0 ? (
            <CleanState theme={theme} audit={latestAudit} />
          ) : (
            <FindingsList
              theme={theme}
              grouped={groupedBySeverity}
              onNavigate={onNavigate}
            />
          )}
        </>
      )}

      {showHistory && (
        <HistoryDrawer
          theme={theme}
          history={history}
          activeId={activeAuditId}
          onClose={() => setShowHistory(false)}
          onSelect={loadAudit}
        />
      )}

      {showReport && latestAudit && (
        <ReportModal
          theme={theme}
          audit={latestAudit}
          onClose={() => setShowReport(false)}
        />
      )}
    </div>
  );
}

// ---------- Helpers ----------

function normalizeAudit(a) {
  if (!a) return null;
  // Backend may return findings as a JSON string depending on driver settings.
  let findings = a.findings;
  if (typeof findings === 'string') {
    try { findings = JSON.parse(findings); } catch (_) { findings = []; }
  }
  if (!Array.isArray(findings)) findings = [];
  let summary = a.summary;
  if (typeof summary === 'string') {
    try { summary = JSON.parse(summary); } catch (_) { summary = {}; }
  }
  return { ...a, findings, summary };
}

function groupBySeverity(findings) {
  const groups = {};
  findings.forEach(f => {
    const sev = SEVERITY_META[f.severity] ? f.severity : 'medium';
    if (!groups[sev]) groups[sev] = [];
    groups[sev].push(f);
  });
  return Object.keys(groups)
    .sort((a, b) => SEVERITY_META[a].order - SEVERITY_META[b].order)
    .map(sev => ({ severity: sev, items: groups[sev] }));
}

// ---------- Sub-components ----------

function Header({ theme, running, latestAudit, onRun, onShowHistory, onShowReport, historyCount }) {
  return (
    <div style={{ marginBottom: 28 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
        <div style={{
          width: 44, height: 44, borderRadius: 12,
          background: 'rgba(14, 165, 233, 0.15)',
          display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}>
          <ShieldCheck size={22} color="#0ea5e9" />
        </div>
        <div>
          <h1 style={{ margin: 0, fontSize: 26, fontWeight: 700 }}>Account Audit</h1>
          <p style={{ margin: '4px 0 0', color: theme.colors.text.secondary, fontSize: 14 }}>
            One scan. All issues. Estimated € impact and exactly where to fix each one.
          </p>
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 16 }}>
        <button
          onClick={onRun}
          disabled={running}
          style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '12px 20px', borderRadius: 10,
            background: running ? theme.colors.bg.tertiary : '#0ea5e9',
            color: running ? theme.colors.text.muted : '#fff',
            border: 'none', cursor: running ? 'not-allowed' : 'pointer',
            fontSize: 14, fontWeight: 600,
            boxShadow: running ? 'none' : '0 4px 12px rgba(14, 165, 233, 0.3)'
          }}
        >
          {running
            ? <><RefreshCw size={16} className="spin" /> Running audit…</>
            : latestAudit
              ? <><RefreshCw size={16} /> Re-run audit</>
              : <><Play size={16} /> Run audit now</>}
        </button>

        {latestAudit && (
          <button
            onClick={onShowReport}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '12px 16px', borderRadius: 10,
              background: 'transparent',
              color: theme.colors.text.primary,
              border: `1px solid ${theme.colors.border.primary}`,
              cursor: 'pointer', fontSize: 13, fontWeight: 600
            }}
          >
            <FileText size={14} /> Generate report
          </button>
        )}

        {historyCount > 0 && (
          <button
            onClick={onShowHistory}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '12px 16px', borderRadius: 10,
              background: 'transparent',
              color: theme.colors.text.secondary,
              border: `1px solid ${theme.colors.border.primary}`,
              cursor: 'pointer', fontSize: 13, fontWeight: 500
            }}
          >
            <HistoryIcon size={14} /> History ({historyCount})
          </button>
        )}

        {latestAudit?.completedAt && (
          <span style={{ fontSize: 12, color: theme.colors.text.muted, marginLeft: 'auto' }}>
            Last run: {formatRelative(latestAudit.completedAt || latestAudit.completed_at)}
            {latestAudit.duration_ms ? ` · ${(latestAudit.duration_ms / 1000).toFixed(1)}s` : ''}
          </span>
        )}
      </div>

      <style>{`@keyframes audit-spin { to { transform: rotate(360deg); } } .spin { animation: audit-spin 1s linear infinite; }`}</style>
    </div>
  );
}

function ErrorBanner({ theme, message, onDismiss }) {
  return (
    <div style={{
      padding: '12px 16px', borderRadius: 10, marginBottom: 20,
      background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.3)',
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      color: theme.mode === 'light' ? '#991b1b' : '#fecaca'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <AlertCircle size={16} color="#ef4444" />
        <span style={{ fontSize: 14 }}>{message}</span>
      </div>
      <button onClick={onDismiss} style={{
        background: 'none', border: 'none', color: 'inherit', cursor: 'pointer'
      }}><X size={16} /></button>
    </div>
  );
}

function EmptyState({ theme, onRun }) {
  return (
    <div style={{
      marginTop: 24, padding: '48px 32px', borderRadius: 16,
      background: theme.colors.bg.secondary,
      border: `1px solid ${theme.colors.border.primary}`,
      textAlign: 'center'
    }}>
      <div style={{
        width: 56, height: 56, borderRadius: 16,
        background: 'rgba(14, 165, 233, 0.15)',
        margin: '0 auto 16px',
        display: 'flex', alignItems: 'center', justifyContent: 'center'
      }}>
        <Sparkles size={26} color="#0ea5e9" />
      </div>
      <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>
        Run your first account audit
      </h3>
      <p style={{
        margin: '8px auto 24px', fontSize: 14, lineHeight: 1.6,
        color: theme.colors.text.secondary, maxWidth: 480
      }}>
        We'll scan tracking, search terms, PMax, bidding strategy and budget health — and
        tell you exactly what's leaking money and where to fix it. No changes are made to
        your Google Ads account.
      </p>
      <button onClick={onRun} style={{
        display: 'inline-flex', alignItems: 'center', gap: 8,
        padding: '12px 22px', borderRadius: 10,
        background: '#0ea5e9', color: '#fff', border: 'none',
        cursor: 'pointer', fontSize: 14, fontWeight: 600
      }}>
        <Play size={16} /> Run audit
      </button>
    </div>
  );
}

function SummaryCards({ theme, audit }) {
  const totalSavings = audit.estimatedSavingsEur || audit.estimated_savings_eur || 0;
  const totalFixTime = audit.estimatedTimeToFixMinutes || audit.estimated_time_to_fix_minutes || 0;
  const total = audit.totalFindings || audit.total_findings || (audit.findings?.length || 0);
  const sev = audit.severityCounts || {
    critical: audit.critical_count || 0,
    high: audit.high_count || 0,
    medium: audit.medium_count || 0,
    low: audit.low_count || 0
  };

  // How long this audit would have taken a human analyst to compile manually.
  // A real analyst doesn't spend a fixed chunk per micro-finding — once you're
  // in the data, each extra issue is incremental. So we cap the per-finding
  // contribution (you stop counting after ~50 issues) and use a modest
  // per-finding rate on top of a fixed audit baseline. Floor at 2h so a clean
  // account still reads honestly ("you'd still spend 2h confirming nothing's
  // broken").
  const manualAuditMin = Math.max(120, Math.min(total, 50) * 12 + 120);
  // What Bidoryx actually spent: the audit run + ~5 min to read the report.
  const bidoryxAuditMin = Math.max(1, Math.round((audit.duration_ms || audit.durationMs || 0) / 60000) + 5);
  const savedMin = Math.max(0, manualAuditMin - bidoryxAuditMin);

  const card = {
    flex: 1, padding: 20, borderRadius: 14,
    background: theme.colors.bg.secondary,
    border: `1px solid ${theme.colors.border.primary}`,
    minWidth: 180
  };

  return (
    <div style={{ display: 'flex', gap: 14, marginBottom: 24, flexWrap: 'wrap' }}>
      <div style={card}>
        <div style={{ fontSize: 12, color: theme.colors.text.tertiary, fontWeight: 500, textTransform: 'uppercase', letterSpacing: 0.4 }}>
          Findings
        </div>
        <div style={{ fontSize: 28, fontWeight: 700, marginTop: 6 }}>{total}</div>
        <div style={{ fontSize: 12, color: theme.colors.text.secondary, marginTop: 4 }}>
          {sev.critical || 0} critical · {sev.high || 0} high · {sev.medium || 0} medium
        </div>
      </div>
      <div style={card}>
        <div style={{ fontSize: 12, color: theme.colors.text.tertiary, fontWeight: 500, textTransform: 'uppercase', letterSpacing: 0.4 }}>
          Potential monthly savings
        </div>
        <div style={{ fontSize: 28, fontWeight: 700, marginTop: 6, color: '#10b981' }}>
          {formatCurrencyEur(totalSavings)}
        </div>
        <div style={{ fontSize: 12, color: theme.colors.text.secondary, marginTop: 4 }}>
          conservative estimate
        </div>
      </div>
      <div style={card}>
        <div style={{ fontSize: 12, color: theme.colors.text.tertiary, fontWeight: 500, textTransform: 'uppercase', letterSpacing: 0.4 }}>
          Time saved by Bidoryx
        </div>
        <div style={{ fontSize: 28, fontWeight: 700, marginTop: 6, color: '#0ea5e9' }}>
          {formatTimeMin(savedMin)}
        </div>
        <div style={{ fontSize: 12, color: theme.colors.text.secondary, marginTop: 4 }}>
          Manual audit ~{formatTimeMin(manualAuditMin)} · Bidoryx ~{formatTimeMin(bidoryxAuditMin)}
        </div>
        {totalFixTime > 0 && (
          <div style={{ fontSize: 11, color: theme.colors.text.muted, marginTop: 6 }}>
            Fix time across all findings: ~{formatTimeMin(totalFixTime)}
          </div>
        )}
      </div>
    </div>
  );
}

function CleanState({ theme, audit }) {
  return (
    <div style={{
      padding: '36px 32px', borderRadius: 16,
      background: theme.colors.bg.secondary,
      border: `1px solid ${theme.colors.border.primary}`,
      textAlign: 'center'
    }}>
      <div style={{
        width: 56, height: 56, borderRadius: 16,
        background: 'rgba(16, 185, 129, 0.15)',
        margin: '0 auto 16px',
        display: 'flex', alignItems: 'center', justifyContent: 'center'
      }}>
        <CheckCircle2 size={28} color="#10b981" />
      </div>
      <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>
        No issues found — your account looks healthy
      </h3>
      <p style={{
        margin: '8px auto 0', fontSize: 14, lineHeight: 1.6,
        color: theme.colors.text.secondary, maxWidth: 460
      }}>
        We didn't find any blocking issues across {audit.summary?.analyzersRun?.length || 0} analyzers.
        Re-run weekly to catch drift early.
      </p>
    </div>
  );
}

function FindingsList({ theme, grouped, onNavigate }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      {grouped.map(({ severity, items }) => {
        const meta = SEVERITY_META[severity];
        return (
          <div key={severity}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '0 4px', marginBottom: 10
            }}>
              <meta.Icon size={16} color={meta.color} />
              <span style={{ fontSize: 13, fontWeight: 600, color: meta.color, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                {meta.label}
              </span>
              <span style={{ fontSize: 12, color: theme.colors.text.muted }}>
                · {items.length} {items.length === 1 ? 'finding' : 'findings'}
              </span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {items.map(f => (
                <FindingCard
                  key={f.id}
                  theme={theme}
                  finding={f}
                  onNavigate={onNavigate}
                />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function FindingCard({ theme, finding, onNavigate }) {
  const meta = SEVERITY_META[finding.severity] || SEVERITY_META.medium;
  const [expanded, setExpanded] = useState(false);

  const hasContext = !!finding.reasoning;

  return (
    <div style={{
      padding: 16, borderRadius: 12,
      background: theme.colors.bg.secondary,
      border: `1px solid ${theme.colors.border.primary}`,
      borderLeft: `3px solid ${meta.color}`
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <div style={{
          flexShrink: 0, width: 32, height: 32, borderRadius: 8,
          background: meta.bg,
          display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}>
          <meta.Icon size={16} color={meta.color} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <h4 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>{finding.title}</h4>
            <span style={{
              fontSize: 11, fontWeight: 500,
              padding: '2px 8px', borderRadius: 6,
              background: theme.colors.bg.tertiary,
              color: theme.colors.text.secondary
            }}>{finding.category}</span>
          </div>

          {finding.description && (
            <p style={{ margin: '8px 0 0', fontSize: 13, lineHeight: 1.55, color: theme.colors.text.secondary }}>
              {finding.description}
            </p>
          )}

          {expanded && finding.reasoning && (
            <p style={{
              margin: '10px 0 0', padding: '10px 12px', borderRadius: 8,
              background: theme.colors.bg.tertiary,
              fontSize: 12, lineHeight: 1.55, color: theme.colors.text.secondary
            }}>
              {finding.reasoning}
            </p>
          )}

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginTop: 12, fontSize: 12, color: theme.colors.text.secondary }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <Euro size={13} />
              <strong style={{ color: '#10b981' }}>{formatCurrencyEur(finding.impactEur)}</strong>
              <span style={{ color: theme.colors.text.muted }}>/mo</span>
            </span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <Clock size={13} />
              <span>{formatTimeMin(finding.timeToFixMinutes)} to fix</span>
            </span>
            {hasContext && (
              <button onClick={() => setExpanded(e => !e)} style={{
                marginLeft: 'auto', background: 'none', border: 'none',
                color: theme.colors.text.muted, cursor: 'pointer',
                fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 2
              }}>
                {expanded ? 'Hide details' : 'Why this matters'}
                <ChevronRight size={12} style={{ transform: expanded ? 'rotate(90deg)' : 'rotate(0)', transition: 'transform 0.15s' }} />
              </button>
            )}
          </div>

          {finding.actionView && (
            <div style={{ display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap' }}>
              <button onClick={() => onNavigate?.(finding.actionView)} style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '7px 12px', borderRadius: 8,
                background: meta.color, color: '#fff', border: 'none',
                cursor: 'pointer', fontSize: 12, fontWeight: 600
              }}>
                {finding.actionLabel || 'Open module'} <ArrowRight size={12} />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ReportModal({ theme, audit, onClose }) {
  const [tone, setTone] = useState('c_level');
  const [polish, setPolish] = useState(true);
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState(null);

  // Refresh the preview whenever tone or polish changes.
  useEffect(() => {
    let alive = true;
    setLoadingPreview(true);
    setError(null);
    (async () => {
      try {
        const data = await apiCallJson(
          `/api/audit/${audit.id}/summary?tone=${tone}&polish=${polish ? 1 : 0}`,
          { timeout: 60000 }
        );
        if (!alive) return;
        if (data?.success) setPreview(data.summary);
        else setError(data?.error || 'Could not generate preview');
      } catch (err) {
        if (alive) setError(err.message || 'Could not generate preview');
      } finally {
        if (alive) setLoadingPreview(false);
      }
    })();
    return () => { alive = false; };
  }, [audit.id, tone, polish]);

  const downloadPdf = async () => {
    setDownloading(true);
    setError(null);
    try {
      const res = await apiCall(
        `/api/audit/${audit.id}/report.pdf?tone=${tone}&polish=${polish ? 1 : 0}`,
        { timeout: 90000, parseJson: false }
      );
      if (!res.ok) throw new Error(`PDF request failed (${res.status})`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const safeName = (audit.accountName || audit.account_name || `audit-${audit.id}`)
        .toString().replace(/[^a-zA-Z0-9-_]+/g, '-').slice(0, 60);
      const a = document.createElement('a');
      a.href = url;
      a.download = `bidoryx-audit-${safeName}-${tone}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      setError(err.message || 'Could not download PDF');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1200,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: theme.colors.bg.primary,
        border: `1px solid ${theme.colors.border.primary}`,
        borderRadius: 14, width: '100%', maxWidth: 720,
        maxHeight: '88vh', display: 'flex', flexDirection: 'column'
      }}>
        <div style={{
          padding: '16px 20px', borderBottom: `1px solid ${theme.colors.border.primary}`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <FileText size={18} color="#0ea5e9" />
            <span style={{ fontSize: 15, fontWeight: 600, color: theme.colors.text.primary }}>
              Audit report
            </span>
            <span style={{ fontSize: 12, color: theme.colors.text.muted }}>
              for {audit.accountName || audit.account_name || `audit #${audit.id}`}
            </span>
          </div>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', color: theme.colors.text.secondary, cursor: 'pointer'
          }}><X size={18} /></button>
        </div>

        <div style={{ padding: 20, overflowY: 'auto' }}>
          {/* Tone toggle */}
          <div style={{ marginBottom: 16 }}>
            <div style={{
              fontSize: 11, fontWeight: 600, letterSpacing: 0.5,
              color: theme.colors.text.muted, textTransform: 'uppercase', marginBottom: 8
            }}>
              Audience
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              {[
                { id: 'c_level', label: 'C-level / boardroom', sub: 'Money, risk, ROI. No jargon.' },
                { id: 'operational', label: 'Operational / PPC', sub: 'Specific to the marketer who fixes it.' }
              ].map(opt => {
                const active = tone === opt.id;
                return (
                  <button
                    key={opt.id}
                    onClick={() => setTone(opt.id)}
                    style={{
                      flex: 1, textAlign: 'left',
                      padding: '12px 14px', borderRadius: 10,
                      background: active ? 'rgba(14, 165, 233, 0.10)' : theme.colors.bg.secondary,
                      border: `1px solid ${active ? '#0ea5e9' : theme.colors.border.primary}`,
                      color: theme.colors.text.primary,
                      cursor: 'pointer'
                    }}
                  >
                    <div style={{ fontSize: 13, fontWeight: 600 }}>{opt.label}</div>
                    <div style={{ fontSize: 11, color: theme.colors.text.muted, marginTop: 3 }}>{opt.sub}</div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Polish toggle */}
          <label style={{
            display: 'flex', alignItems: 'center', gap: 10,
            padding: '10px 12px', borderRadius: 10,
            background: theme.colors.bg.secondary,
            border: `1px solid ${theme.colors.border.primary}`,
            cursor: 'pointer', marginBottom: 18
          }}>
            <input
              type="checkbox"
              checked={polish}
              onChange={e => setPolish(e.target.checked)}
              style={{ accentColor: '#0ea5e9', width: 16, height: 16 }}
            />
            <Wand2 size={14} color="#0ea5e9" />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: theme.colors.text.primary }}>
                AI natural rewrite
              </div>
              <div style={{ fontSize: 11, color: theme.colors.text.muted }}>
                Polishes the prose with gpt-4o-mini. Numbers and findings stay verbatim.
              </div>
            </div>
          </label>

          {/* Preview */}
          <div style={{
            padding: 16, borderRadius: 10,
            background: theme.colors.bg.secondary,
            border: `1px solid ${theme.colors.border.primary}`,
            minHeight: 180
          }}>
            {loadingPreview ? (
              <div style={{ color: theme.colors.text.muted, fontSize: 13 }}>
                <RefreshCw size={14} className="spin" style={{ verticalAlign: 'middle', marginRight: 6 }} />
                Generating preview…
              </div>
            ) : error ? (
              <div style={{ color: '#ef4444', fontSize: 13 }}>{error}</div>
            ) : preview ? (
              <ReportPreview theme={theme} summary={preview} />
            ) : null}
          </div>
        </div>

        <div style={{
          padding: '14px 20px', borderTop: `1px solid ${theme.colors.border.primary}`,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10
        }}>
          <span style={{ fontSize: 11, color: theme.colors.text.muted }}>
            {preview?.polished ? 'Narrative AI-assisted · numbers verbatim' : 'Deterministic narrative'}
          </span>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={onClose}
              style={{
                padding: '10px 16px', borderRadius: 10,
                background: 'transparent', color: theme.colors.text.secondary,
                border: `1px solid ${theme.colors.border.primary}`,
                cursor: 'pointer', fontSize: 13
              }}
            >
              Close
            </button>
            <button
              onClick={downloadPdf}
              disabled={downloading || loadingPreview}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '10px 18px', borderRadius: 10,
                background: downloading ? theme.colors.bg.tertiary : '#0ea5e9',
                color: downloading ? theme.colors.text.muted : '#fff',
                border: 'none', fontSize: 13, fontWeight: 600,
                cursor: downloading ? 'not-allowed' : 'pointer'
              }}
            >
              {downloading
                ? <><RefreshCw size={14} className="spin" /> Building PDF…</>
                : <><Download size={14} /> Download PDF</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ReportPreview({ theme, summary }) {
  const isCLevel = summary.tone === 'c_level';
  const sectionLabelStyle = {
    fontSize: 11, fontWeight: 600, letterSpacing: 0.5,
    color: theme.colors.text.muted, textTransform: 'uppercase', marginBottom: 6
  };

  return (
    <div>
      <div style={sectionLabelStyle}>
        {isCLevel ? 'Strategic memo' : 'Operational summary'}
      </div>
      {isCLevel && summary.accountState && (
        <div style={{
          display: 'inline-block',
          fontSize: 11, fontWeight: 600,
          padding: '3px 10px', borderRadius: 6, marginBottom: 8,
          background: 'rgba(14, 165, 233, 0.10)',
          color: '#0369a1'
        }}>
          {summary.accountState}
        </div>
      )}
      <div style={{
        fontSize: 14, fontWeight: 700, color: '#0369a1', lineHeight: 1.4, marginBottom: 10
      }}>
        {summary.headline}
      </div>
      <div style={{
        fontSize: 12, lineHeight: 1.6, color: theme.colors.text.secondary,
        whiteSpace: 'pre-wrap', marginBottom: 14
      }}>
        {summary.executive}
      </div>

      {/* C-level: strategic opportunities (with framing) */}
      {isCLevel && summary.opportunities?.length > 0 && (
        <div style={{ marginBottom: 14 }}>
          <div style={sectionLabelStyle}>Strategic opportunities</div>
          {summary.opportunities.map((o, idx) => (
            <div key={idx} style={{
              padding: '8px 0', borderBottom: `1px solid ${theme.colors.border.secondary}`
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: theme.colors.text.primary }}>
                  {idx + 1}. {o.title}
                </div>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#10b981', whiteSpace: 'nowrap' }}>
                  €{Math.round(o.impact || 0).toLocaleString('nl-NL')}/mo
                </div>
              </div>
              {o.framing && (
                <div style={{
                  fontSize: 11, lineHeight: 1.55, color: theme.colors.text.secondary, marginTop: 3
                }}>
                  {o.framing}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* C-level: decisions needed */}
      {isCLevel && summary.decisions?.length > 0 && (
        <div style={{ marginBottom: 14 }}>
          <div style={sectionLabelStyle}>Decisions needed</div>
          {summary.decisions.map((d, idx) => (
            <div key={idx} style={{
              fontSize: 11, lineHeight: 1.55, color: theme.colors.text.secondary,
              padding: '8px 10px', borderRadius: 6, marginTop: 6,
              background: theme.colors.bg.tertiary
            }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: theme.colors.text.primary, marginBottom: 2 }}>
                {d.title}
              </div>
              {d.context}
            </div>
          ))}
        </div>
      )}

      {/* Operational: top findings list */}
      {!isCLevel && summary.keyIssues?.length > 0 && (
        <div style={{ marginBottom: 14 }}>
          <div style={sectionLabelStyle}>Top {summary.keyIssues.length} findings</div>
          {summary.keyIssues.map((i, idx) => (
            <div key={idx} style={{
              fontSize: 12, color: theme.colors.text.secondary,
              padding: '6px 0', borderBottom: `1px solid ${theme.colors.border.secondary}`
            }}>
              <span style={{ color: theme.colors.text.primary, fontWeight: 600 }}>
                {idx + 1}. {i.title}
              </span>
              <span style={{ color: theme.colors.text.muted, marginLeft: 6 }}>
                · {i.category} · €{Math.round(i.impact || 0).toLocaleString('nl-NL')}/mo
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Both: priorities / next steps */}
      {(isCLevel ? summary.priorities : summary.nextSteps)?.length > 0 && (
        <div>
          <div style={sectionLabelStyle}>
            {isCLevel ? 'Next 30 days' : 'Next steps'}
          </div>
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, color: theme.colors.text.secondary, lineHeight: 1.6 }}>
            {(isCLevel ? summary.priorities : summary.nextSteps).map((s, idx) => (
              <li key={idx}>{s}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function HistoryDrawer({ theme, history, activeId, onClose, onSelect }) {
  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1100,
      display: 'flex', justifyContent: 'flex-end'
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        width: 420, maxWidth: '100%',
        background: theme.colors.bg.primary,
        borderLeft: `1px solid ${theme.colors.border.primary}`,
        height: '100%', overflowY: 'auto'
      }}>
        <div style={{
          padding: '18px 20px', borderBottom: `1px solid ${theme.colors.border.primary}`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between'
        }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>Audit history</h3>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', color: theme.colors.text.secondary, cursor: 'pointer'
          }}><X size={18} /></button>
        </div>
        <div>
          {history.length === 0 ? (
            <div style={{ padding: '32px 20px', color: theme.colors.text.muted, fontSize: 13, textAlign: 'center' }}>
              No previous audits yet.
            </div>
          ) : history.map(a => (
            <button
              key={a.id}
              onClick={() => onSelect(a.id)}
              style={{
                width: '100%', textAlign: 'left',
                padding: '14px 20px',
                background: a.id === activeId ? theme.colors.bg.secondary : 'transparent',
                border: 'none', borderBottom: `1px solid ${theme.colors.border.secondary}`,
                cursor: 'pointer', color: theme.colors.text.primary
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <span style={{ fontSize: 13, fontWeight: 600 }}>
                  {a.account_name || a.account_id}
                </span>
                <span style={{ fontSize: 11, color: theme.colors.text.muted }}>
                  {formatRelative(a.started_at)}
                </span>
              </div>
              <div style={{ fontSize: 12, color: theme.colors.text.secondary, marginTop: 4 }}>
                {a.total_findings || 0} findings · {formatCurrencyEur(a.estimated_savings_eur)}/mo · {a.status}
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

